/** Option shape required for grouping: an optional `group` heading. */
export interface GroupableSelectOption {
  /**
   * Optional group. Rows sharing a value render together as a section with
   * the value as its heading. `undefined`/`''` render a heading-less group;
   * distinct empties bucket separately so a sentinel (e.g. "None") can pin
   * above the rest.
   */
  group?: string;
}

export interface SelectOptionGroup<T> {
  heading?: string;
  options: T[];
}

/** Bucket options into render groups, preserving first-seen group order. */
export function groupOptions<T extends GroupableSelectOption>(
  options: readonly T[],
): SelectOptionGroup<T>[] {
  const groups: SelectOptionGroup<T>[] = [];
  const byKey = new Map<string, SelectOptionGroup<T>>();
  for (const opt of options) {
    // Distinct empties (`undefined` vs `''`) bucket separately so a sentinel
    // group and a heading-less options group can coexist.
    const key = opt.group === undefined ? '\u0000undefined' : `h:${opt.group}`;
    let group = byKey.get(key);
    if (!group) {
      group = { heading: opt.group ? opt.group : undefined, options: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.options.push(opt);
  }
  return groups;
}

/**
 * Reorder each group so the selected option sits first. A sentinel in its
 * own group is unaffected because reordering is scoped per group.
 */
export function hoistSelectedFirst<T>(
  groups: SelectOptionGroup<T>[],
  isSelected: (option: T) => boolean,
): SelectOptionGroup<T>[] {
  return groups.map((group) => {
    const index = group.options.findIndex(isSelected);
    if (index <= 0) return group;
    const next = group.options.slice();
    const [selected] = next.splice(index, 1);
    next.unshift(selected!);
    return { heading: group.heading, options: next };
  });
}
